import { eventDetailQuery } from '@/api/queries'
import type { EventDetailResponse } from '@/api/schemas'
import { Drawer, DrawerBody, DrawerHeader } from '@/components/ui/Drawer'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import { formatLocalDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect } from 'react'
import { t } from '@/lib/i18n'

interface Props {
  siteId: string
  eventId: number
  /** Camera-scoped prev/next when set. */
  cameraId?: string | null
  /** Ordered event ids to navigate within. When provided, ←/→ and the
   *  drawer's prev/next buttons walk this list instead of the
   *  server-returned prev_id/next_id. Used by the photos sala so arrow
   *  keys cycle through the captures visible on screen, not the full
   *  camera/site history. */
  navList?: readonly number[]
  onClose: () => void
  onNavigate: (nextEventId: number) => void
}

export function EventDrawer({
  siteId,
  eventId,
  cameraId,
  navList,
  onClose,
  onNavigate,
}: Props) {
  const query = useQuery(eventDetailQuery(siteId, eventId, cameraId ?? null))

  const [prevId, nextId] = resolveNav(
    navList,
    eventId,
    query.data?.prev_id ?? null,
    query.data?.next_id ?? null,
  )

  // Keyboard nav: ←/→ between prev/next.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prevId) onNavigate(prevId)
      if (e.key === 'ArrowRight' && nextId) onNavigate(nextId)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [prevId, nextId, onNavigate])

  return (
    <Drawer open onClose={onClose} label={t('Event detail')}>
      {query.isPending ? (
        <div className="p-4">
          <LoadingCard label={t('Loading event…')} />
        </div>
      ) : query.isError ? (
        <div className="p-4">
          <ErrorCard message={query.error.message} onRetry={query.refetch} />
        </div>
      ) : (
        <EventBody
          siteId={siteId}
          data={query.data}
          prevId={prevId}
          nextId={nextId}
          onClose={onClose}
          onNavigate={onNavigate}
        />
      )}
    </Drawer>
  )
}

function resolveNav(
  navList: readonly number[] | undefined,
  current: number,
  serverPrev: number | null,
  serverNext: number | null,
): [number | null, number | null] {
  if (!navList || navList.length === 0) return [serverPrev, serverNext]
  const i = navList.indexOf(current)
  if (i === -1) return [serverPrev, serverNext]
  const prev = i > 0 ? (navList[i - 1] ?? null) : null
  const next = i < navList.length - 1 ? (navList[i + 1] ?? null) : null
  return [prev, next]
}

function EventBody({
  siteId,
  data,
  prevId,
  nextId,
  onClose,
  onNavigate,
}: {
  siteId: string
  data: EventDetailResponse
  prevId: number | null
  nextId: number | null
  onClose: () => void
  onNavigate: (id: number) => void
}) {
  const { event } = data
  return (
    <>
      <DrawerHeader onClose={onClose}>
        <NavButton
          direction="prev"
          disabled={!prevId}
          onClick={() => prevId && onNavigate(prevId)}
        />
        <NavButton
          direction="next"
          disabled={!nextId}
          onClick={() => nextId && onNavigate(nextId)}
        />
        <span className="text-sm font-medium text-foreground ml-1 truncate">{event.camera_id}</span>
        <span className="text-xs text-subtle-foreground truncate">
          {formatLocalDateTime(event.timestamp)}
        </span>
        <code className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
          {event.trigger}
        </code>
        {event.relevance ? (
          <span
            className={cn(
              'text-xs',
              event.relevance === 'high' && 'text-critical',
              event.relevance === 'medium' && 'text-attention',
              event.relevance !== 'high' && event.relevance !== 'medium' && 'text-subtle-foreground',
            )}
          >
            {event.relevance}
          </span>
        ) : null}
      </DrawerHeader>
      <DrawerBody>
        {event.has_image ? (
          <div className="bg-muted">
            <img
              src={`/api/sites/${encodeURIComponent(siteId)}/events/${event.id}/image`}
              alt={t('Event {id}', { id: event.id })}
              className="w-full max-h-[60vh] object-contain"
            />
          </div>
        ) : (
          <div className="bg-muted flex items-center justify-center py-16 text-subtle-foreground text-sm">
            {t('No image')}
          </div>
        )}
        {event.description ? (
          <div className="px-4 py-3 border-t border-border-subtle">
            <p className="text-sm text-foreground">{event.description}</p>
            {event.alert ? (
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={cn(
                    'w-2 h-2 rounded-full',
                    event.alert.level === 'critical' ? 'bg-critical' : 'bg-attention',
                  )}
                />
                <span
                  className={cn(
                    'text-xs font-medium',
                    event.alert.level === 'critical' ? 'text-critical' : 'text-attention',
                  )}
                >
                  {event.alert.message}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </DrawerBody>
    </>
  )
}

function NavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next'
  disabled: boolean
  onClick: () => void
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-7 h-7 rounded-lg border flex items-center justify-center transition-colors',
        disabled
          ? 'border-border-subtle text-inverse-soft cursor-not-allowed'
          : 'border-border text-subtle-foreground hover:text-foreground-soft hover:border-input',
      )}
      aria-label={direction === 'prev' ? t('Previous event') : t('Next event')}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}
